import styles from "./SubscriptionItem.module.scss";
import {
  IModelForBuyingSubscription,
  IModelForChangingSubscription,
  ISubscription,
  SubscriptionDurationTypesEnum,
} from "../../../../../models/Subscription";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import CustomButton from "../../../../../components/ui/CustomButton/CustomButton";
import useAppSelector from "../../../../../hooks/useAppSelector";
import useAppDispatch from "../../../../../hooks/useAppDispatch";
import { openErrorMessage } from "../../../../../store/message/messageSlice";
import { UserSubscriptionService } from "../../../../../services/userSubscription.service";
import { setUserSubscription } from "../../../../../store/userSubscription/userSubscriptionSlice";
import ConfirmPopup from "../../../../../components/shared/ConfirmPopup/ConfirmPopup";
import attentionIcon from "../../../../../assets/images/icons/attention.svg";
import buySubscriptionIcon from "../../../../../assets/images/icons/buy-subscription.svg";
import { useNavigate } from "react-router-dom";
import { handleError } from "../../../../../helper/errorHandler";
import { PaymixService } from "../../../../../services/paymix.service";
import { getSubscriptionDurationType } from "../../../../../helper/getSubscriptionDurationType";

interface ISubscriptionItemProps {
  item: ISubscription;
}

const SubscriptionItem: React.FC<ISubscriptionItemProps> = ({ item }) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const { user } = useAppSelector((state) => state.userProfileSlice);
  const { defaultPaymentMethod } = useAppSelector((state) => state.userPaymentMethodsSlice);
  const { userSubscription } = useAppSelector((state) => state.userSubscriptionSlice);
  const [showMore, setShowMore] = useState<boolean>(false);
  const [showConfirmPopup, setShowConfirmPopup] = useState({
    isOpen: false,
    title: "",
    description: "",
    icon: attentionIcon,
  });
  const [buttonLoader, setButtonLoader] = useState(false);

  const getUserSubscription = async (userId: string) => {
    try {
      const response = await UserSubscriptionService.getUserSubscriptionByUserId(userId);
      dispatch(setUserSubscription(response.data));
    } catch (e) {}
  };

  const clickOnSelectPlan = () => {
    if (!userSubscription) {
      setShowConfirmPopup({
        isOpen: true,
        title: t("ConfirmYourPurchase"),
        description: t("AreYouSureYouWantToPurchaseThisParticularSubscription"),
        icon: buySubscriptionIcon,
      });
    } else {
      setShowConfirmPopup({
        isOpen: true,
        title: t("ChangeYourSubscription") + "!",
        description: t("AreYouSureYouWantToChangeYourSubscription"),
        icon: attentionIcon,
      });
    }
  };

  const buyOrChangeSubscription = async () => {
    if (!user) {
      return;
    }

    // TODO maybe change the logic
    if (!defaultPaymentMethod) {
      navigate("/settings/billing");
      return;
    }

    if (!userSubscription) {
      const model: IModelForBuyingSubscription = {
        userId: user.id,
        paymentMethodId: defaultPaymentMethod.id,
        subscriptionId: item.id,
        loyaltyExchangeId: null,
        // TODO change redirect url
        redirectUrl: "https://clubpass.gr",
      };
      buySubscription(model);
    } else {
      // const model: IModelForChangingSubscription = {
      //     userId: user.id,
      //     paymentMethodId: defaultPaymentMethod.id,
      //     subscriptionId: item.id,
      //     userSubscriptionId: userSubscription.id
      // }
      // changeSubscription(model);
    }
  };

  const buySubscription = async (model: IModelForBuyingSubscription) => {
    setButtonLoader(true);
    try {
      const { data } = await PaymixService.buySubscription(model);
      if (data.isSuccess) {
        if (data.redirectUrl) {
          window.open(data.redirectUrl, "_blank");
        } else {
          await getUserSubscription(model.userId);
          hideConfirmPopup();
        }
      } else {
        dispatch(openErrorMessage(t("FailedToBuySubscription")));
      }
    } catch (e) {
      handleError(e, dispatch, t);
    }
    setButtonLoader(false);
  };

  // const changeSubscription = async (model: IModelForChangingSubscription) => {
  //   try {
  //     const response = await UserSubscriptionService.changeSubscription(model);
  //
  //     if (response.data && response.status === 200) {
  //       dispatch(
  //         openMessage({
  //           isOpen: true,
  //           type: "success",
  //           title: t("SubscriptionChangedSuccessfully"),
  //         })
  //       );
  //       setTimeout(() => {
  //         getUserSubscription(model.userId);
  //         hideConfirmPopup();
  //       }, 1000);
  //     }
  //   } catch (e: any) {
  //     handleError(e, dispatch, t);
  //   }
  // };

  const hideConfirmPopup = () => {
    setShowConfirmPopup({ isOpen: false, title: "", description: "", icon: attentionIcon });
  };

  const changeShowMoreVisible = () => {
    setShowMore(!showMore);
  };

  return (
    <>
      <div className={styles.item}>
        <div className={styles.box}>
          <h3 className={styles.name}>{item.name}</h3>
          <h3 className={styles.credits}>
            {item.credits} {t("Credits")}
          </h3>
          <p className={styles.price}>
            €{item.price} / {item.duration}{" "}
            {getSubscriptionDurationType(item.durationType, item.duration, t)}
          </p>
          {item.description.length > 37 && !showMore ? (
            <p className={styles.description}>
              {item.description.slice(0, 33)}{" "}
              <span onClick={changeShowMoreVisible} className={styles.showMore}>
                {t("ShowMore")}..
              </span>
            </p>
          ) : (
            <p className={styles.description}>
              {item.description}
              {showMore && (
                <span onClick={changeShowMoreVisible} className={styles.showMore}>
                  {t("ShowLess")}
                </span>
              )}
            </p>
          )}
        </div>
        <div className={styles.btnWrapper}>
          <CustomButton
            functionOnClick={clickOnSelectPlan}
            text={t("SelectThisPlan")}
            type={"button"}
            typeStyle={"fill"}
          />
        </div>
      </div>
      {showConfirmPopup.isOpen && (
        <ConfirmPopup
          iconPath={showConfirmPopup.icon}
          title={showConfirmPopup.title}
          description={showConfirmPopup.description}
          onClickYes={buyOrChangeSubscription}
          onClickNo={hideConfirmPopup}
          yesButtonLoader={buttonLoader}
        />
      )}
    </>
  );
};

export default SubscriptionItem;
